(function () {
  'use strict';

  let requestTimer = null;

  function normalise(value) {
    return String(value || '').trim().toLowerCase();
  }

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(normalise)
      .filter(Boolean);
  }

  function parseDate(value, endOfDay) {
    if (!value) return null;

    const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';
    const parsed = new Date(`${value}${value.includes('T') ? '' : suffix}`);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function readConfig() {
    const node = document.getElementById('LSCartIncentiveConfig');

    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(
        '[Love Smiths] Invalid cart incentive configuration:',
        error
      );

      return null;
    }
  }

  function replaceTokens(template, values) {
    return Object.entries(values).reduce((text, entry) => {
      return text.split(`[${entry[0]}]`).join(String(entry[1]));
    }, String(template || ''));
  }

  function formatMoney(cents, currency) {
    const locale = document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${
        currency || ''
      }`.trim();
    }
  }

  function itemExcluded(item, rule) {
    const excludedTypes = splitList(rule.excluded_product_types);
    const excludedHandles = splitList(rule.excluded_product_handles);

    return (
      excludedTypes.includes(normalise(item.product_type)) ||
      excludedHandles.includes(normalise(item.handle))
    );
  }

  function itemMatches(item, rule) {
    if (itemExcluded(item, rule)) return false;

    const values = splitList(rule.match_value);

    if (values.length === 0) return true;

    if (rule.rule_type.indexOf('product_type_') === 0) {
      return values.includes(normalise(item.product_type));
    }

    if (rule.rule_type.indexOf('product_handle_') === 0) {
      return values.includes(normalise(item.handle));
    }

    return true;
  }

  function getLineAmount(item, basis) {
    if (basis === 'discounted') {
      return Number(item.final_line_price ?? item.line_price ?? 0);
    }

    return Number(item.original_line_price ?? item.line_price ?? 0);
  }

  function calculateCurrentValue(cart, rule) {
    const items = Array.isArray(cart.items) ? cart.items : [];

    switch (rule.rule_type) {
      case 'cart_quantity':
        return {
          unit: 'quantity',
          current: items
            .filter((item) => !itemExcluded(item, rule))
            .reduce(
              (total, item) => total + Number(item.quantity || 0),
              0
            ),
          threshold: Number(rule.threshold || 0),
        };

      case 'product_type_quantity':
      case 'product_handle_quantity':
        return {
          unit: 'quantity',
          current: items
            .filter((item) => itemMatches(item, rule))
            .reduce(
              (total, item) => total + Number(item.quantity || 0),
              0
            ),
          threshold: Number(rule.threshold || 0),
        };

      case 'product_type_subtotal':
      case 'product_handle_subtotal':
        return {
          unit: 'money',
          current: items
            .filter((item) => itemMatches(item, rule))
            .reduce(
              (total, item) =>
                total + getLineAmount(item, rule.subtotal_basis),
              0
            ),
          threshold: Math.round(Number(rule.threshold || 0) * 100),
        };

      case 'cart_subtotal':
      default: {
        const hasExclusions =
          splitList(rule.excluded_product_types).length > 0 ||
          splitList(rule.excluded_product_handles).length > 0;

        return {
          unit: 'money',

          current: hasExclusions
            ? items
                .filter((item) => !itemExcluded(item, rule))
                .reduce(
                  (total, item) =>
                    total + getLineAmount(item, rule.subtotal_basis),
                  0
                )
            : rule.subtotal_basis === 'discounted'
              ? Number(
                  cart.total_price ??
                    cart.items_subtotal_price ??
                    0
                )
              : Number(
                  cart.original_total_price ??
                    cart.items_subtotal_price ??
                    0
                ),

          threshold: Math.round(Number(rule.threshold || 0) * 100),
        };
      }
    }
  }

  function rewardLabel(rule, config) {
    const strings = config.strings || {};
    const currency = config.currency || 'EUR';

    switch (rule.reward_type) {
      case 'free_shipping':
        return strings.reward_free_shipping || 'free delivery';

      case 'percentage_discount':
        return replaceTokens(
          strings.reward_percentage_discount || '[value]% off',
          {
            value: Number(rule.reward_value || 0),
          }
        );

      case 'fixed_discount':
        return replaceTokens(
          strings.reward_fixed_discount || '[amount] off',
          {
            amount: formatMoney(
              Math.round(Number(rule.reward_value || 0) * 100),
              currency
            ),
          }
        );

      case 'custom':
      default:
        return (
          rule.custom_reward_label ||
          strings.reward_custom ||
          'your reward'
        );
    }
  }

  function isRuleActive(rule, config) {
    if (!rule.active) return false;
    if (Number(rule.threshold || 0) <= 0) return false;

    const now = new Date();
    const start = parseDate(rule.start_date, false);
    const end = parseDate(rule.end_date, true);

    if (start && now < start) return false;
    if (end && now > end) return false;

    const countries = splitList(rule.country_codes);

    if (
      countries.length &&
      !countries.includes(normalise(config.country))
    ) {
      return false;
    }

    const currencies = splitList(rule.currency_codes);

    if (
      currencies.length &&
      !currencies.includes(normalise(config.currency))
    ) {
      return false;
    }

    return true;
  }

  function evaluateRules(cart, config) {
    return (config.rules || [])
      .filter((rule) => isRuleActive(rule, config))
      .map((rule) => {
        const values = calculateCurrentValue(cart, rule);
        const remaining = Math.max(
          values.threshold - values.current,
          0
        );

        const progress =
          values.threshold > 0
            ? Math.min(
                (values.current / values.threshold) * 100,
                100
              )
            : 0;

        return {
          ...rule,
          ...values,
          remaining,
          progress,
          reached: values.current >= values.threshold,
          reward: rewardLabel(rule, config),
        };
      });
  }

  function sortRules(results) {
    return [...results].sort((a, b) => {
      return (
        Number(a.priority || 100) -
          Number(b.priority || 100) ||
        Number(a.threshold || 0) -
          Number(b.threshold || 0)
      );
    });
  }

  function chooseCompactResults(results) {
    const reached = results
      .filter(
        (rule) =>
          rule.reached &&
          rule.show_when_unlocked
      )
      .sort((a, b) => {
        return (
          Number(a.priority || 100) -
            Number(b.priority || 100) ||
          b.threshold - a.threshold
        );
      });

    const upcoming = results
      .filter((rule) => !rule.reached)
      .sort((a, b) => {
        return (
          Number(a.priority || 100) -
            Number(b.priority || 100) ||
          b.progress - a.progress
        );
      });

    return {
      unlocked: reached[0] || null,
      next: upcoming[0] || null,
    };
  }

  function createMessage(text, className) {
    const paragraph = document.createElement('p');

    paragraph.className = className;
    paragraph.textContent = text;

    return paragraph;
  }

  function createProgress(rule, strings) {
    const progress = document.createElement('progress');

    progress.max = 100;
    progress.value = Math.round(rule.progress);

    progress.setAttribute(
      'aria-label',
      replaceTokens(
        strings.progress_label ||
          '[progress]% progress towards [reward].',
        {
          progress: Math.round(rule.progress),
          reward: rule.reward,
        }
      )
    );

    return progress;
  }

  function createUpcomingMessage(rule, strings, currency) {
    if (rule.unit === 'money') {
      return replaceTokens(
        strings.amount_remaining ||
          'Add [amount] more to unlock [reward].',
        {
          amount: formatMoney(rule.remaining, currency),
          reward: rule.reward,
        }
      );
    }

    return replaceTokens(
      strings.quantity_remaining ||
        'Add [count] more to unlock [reward].',
      {
        count: Math.ceil(rule.remaining),
        reward: rule.reward,
      }
    );
  }

  function renderCompactTarget(target, results, config) {
    const strings = config.strings || {};
    const currency = config.currency || 'EUR';
    const selected = chooseCompactResults(results);
    const fragment = document.createDocumentFragment();

    if (!selected.unlocked && !selected.next) {
      target.hidden = true;
      return;
    }

    if (selected.unlocked) {
      fragment.appendChild(
        createMessage(
          replaceTokens(
            strings.unlocked || '[reward] unlocked.',
            {
              reward: selected.unlocked.reward,
            }
          ),
          'caption-large'
        )
      );
    }

    if (selected.next) {
      fragment.appendChild(
        createMessage(
          createUpcomingMessage(
            selected.next,
            strings,
            currency
          ),
          'caption-large'
        )
      );

      fragment.appendChild(
        createProgress(selected.next, strings)
      );
    }

    target.appendChild(fragment);
    target.hidden = false;
  }

  function renderFullTarget(target, results, config) {
    const strings = config.strings || {};
    const currency = config.currency || 'EUR';

    const visibleRules = sortRules(results).filter(
      (rule) =>
        !rule.reached ||
        rule.show_when_unlocked
    );

    const fragment = document.createDocumentFragment();

    if (!visibleRules.length) {
      target.hidden = true;
      return;
    }

    visibleRules.forEach((rule) => {
      const item = document.createElement('div');

      item.className = 'ls-cart-incentive-item';

      if (rule.reached) {
        item.appendChild(
          createMessage(
            replaceTokens(
              strings.unlocked || '[reward] unlocked.',
              {
                reward: rule.reward,
              }
            ),
            'caption-large'
          )
        );
      } else {
        item.appendChild(
          createMessage(
            createUpcomingMessage(
              rule,
              strings,
              currency
            ),
            'caption-large'
          )
        );

        item.appendChild(
          createProgress(rule, strings)
        );
      }

      fragment.appendChild(item);
    });

    target.appendChild(fragment);
    target.hidden = false;
  }

  function renderTarget(target, results, config) {
    target.innerHTML = '';

    if (target.dataset.mode === 'full') {
      renderFullTarget(target, results, config);
      return;
    }

    renderCompactTarget(target, results, config);
  }

  function render(cartData) {
    const config = readConfig();

    const targets = document.querySelectorAll(
      '[data-ls-cart-incentives]'
    );

    if (!config || !targets.length || !cartData) return;

    const results = evaluateRules(cartData, config);

    targets.forEach((target) => {
      renderTarget(target, results, config);
    });
  }

  async function fetchCartAndRender() {
    try {
      let cart = null;

      if (window.LSCartState) {
        cart = await window.LSCartState.getCart({ force: false });
      } else {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        cart = await response.json();
      }

      render(cart);
    } catch (error) {
      console.error('[Love Smiths] Cart incentive error:', error);
    }
  }

  function isFullCart(cartData) {
    if (window.LSCartState) return window.LSCartState.isFullCart(cartData);

    return Boolean(
      cartData &&
        Array.isArray(cartData.items) &&
        typeof cartData.item_count === 'number' &&
        typeof cartData.total_price === 'number'
    );
  }

  function schedule(cartData) {
    window.clearTimeout(requestTimer);

    requestTimer = window.setTimeout(() => {
      if (isFullCart(cartData)) render(cartData);
      else fetchCartAndRender();
    }, 30);
  }

  document.addEventListener('ls:cart-updated', (event) => {
    schedule(event.detail?.cartData);
  });

  document.addEventListener('ls:cart-rendered', (event) => {
    schedule(event.detail?.cartData);
  });

  function initialise() {
    if (document.querySelector('[data-ls-cart-incentives][data-mode="full"]')) {
      schedule();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();